import React from "react";
import chevronRight from "../../assets/chevron-right.svg";
import chevronLeft from "../../assets/chevron-left.svg";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";

const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const { language } = useLanguage();
    
    const getPageNumbers = () => {
        const pageNumbers = [];

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            if (currentPage <= 3) {
                pageNumbers.push(1, 2, 3, '...', totalPages);
            } else if (currentPage >= totalPages - 2) {
                pageNumbers.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
            } else {
                pageNumbers.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }

        return pageNumbers;
    };

    const pages = getPageNumbers();

    return (
        <div className="flex justify-center mt-6 mb-10">
            <div className="inline-flex items-center gap-2 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-full px-3 py-2 shadow-sm">
                <button
                    className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-semibold transition-colors ${
                        currentPage === 1
                            ? "text-gray-400 dark:text-[#6e7681] cursor-not-allowed"
                            : "text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#1c2128]"
                    }`}
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                    aria-label={getTranslation('pagination.previous', language)}
                >
                    <img
                        className="w-4 h-4 dark:invert dark:opacity-80"
                        src={chevronLeft || ""}
                        alt={getTranslation('pagination.previous', language)}
                        onError={(e) => {
                            console.error("Failed to load chevronLeft icon:", chevronLeft);
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                        }}
                    />
                </button>

                {pages.map((page, index) =>
                    typeof page === "number" ? (
                        <button
                            key={index}
                            className={`min-w-[36px] h-9 px-3 rounded-full text-sm font-semibold transition-colors ${
                                currentPage === page
                                    ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm"
                                    : "text-gray-800 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#1c2128]"
                            }`}
                            onClick={() => onPageChange(page)}
                            aria-current={currentPage === page ? "page" : undefined}
                        >
                            {page}
                        </button>
                    ) : (
                        <span key={index} className="px-2 text-sm text-gray-500 dark:text-[#8b949e] select-none">…</span>
                    )
                )}

                <button
                    className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-semibold transition-colors ${
                        currentPage === totalPages
                            ? "text-gray-400 dark:text-[#6e7681] cursor-not-allowed"
                            : "text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#1c2128]"
                    }`}
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                    aria-label={getTranslation('pagination.next', language)}
                >
                    <img
                        className="w-4 h-4 dark:invert dark:opacity-80"
                        src={chevronRight || ""}
                        alt={getTranslation('pagination.next', language)}
                        onError={(e) => {
                            console.error("Failed to load chevronRight icon:", chevronRight);
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                        }}
                    />
                </button>
            </div>
        </div>
    );
};

export default Pagination;
